import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiX, FiExternalLink } from 'react-icons/fi';
import toast from 'react-hot-toast';

const ToastContainer = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  max-width: 350px;

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
`;

const ToastCard = styled.div`
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.95), rgba(236, 72, 153, 0.95));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  color: white;
  box-shadow: 0 20px 40px rgba(139, 92, 246, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ToastTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const ToastContent = styled.div`
  margin-bottom: 1rem;
`;

const ToastText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  opacity: 0.9;
`;

const ToastActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const PrimaryActionButton = styled(ActionButton)`
  background: rgba(255, 255, 255, 0.9);
  color: #8b5cf6;
  font-weight: 600;

  &:hover {
    background: white;
    color: #8b5cf6;
  }
`;

const FloatingDot = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  top: 1rem;
  right: 1rem;
`;

const BookingToast: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Show toast after 3 seconds of page load
    const timer = setTimeout(() => {
      if (!hasShown) {
        setIsVisible(true);
        setHasShown(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleBookNow = () => {
    // Show a toast notification
    toast.success('Redirecting to booking page...');
    
    // Simulate redirect to cal.com
    setTimeout(() => {
      window.open('https://cal.com/briddy.tech', '_blank');
    }, 1000);
    
    setIsVisible(false);
  };

  const handleLearnMore = () => {
    toast.success('Scrolling to About section...');
    setIsVisible(false);
    // Scroll to About section
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <ToastContainer
          initial={{ opacity: 0, x: 300, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.8 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 30 
          }}
        >
          <ToastCard>
            <FloatingDot
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity 
              }}
            />
            
            <ToastHeader>
              <ToastTitle>
                <FiCalendar size={18} />
                Book a Session
              </ToastTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={18} />
              </CloseButton>
            </ToastHeader>

            <ToastContent>
              <ToastText>
                Ready to collaborate? Book a consultation session to discuss your project 
                and explore how we can work together.
              </ToastText>
            </ToastContent>

            <ToastActions>
              <ActionButton
                onClick={handleLearnMore}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </ActionButton>
              <PrimaryActionButton
                onClick={handleBookNow}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiExternalLink size={16} />
                Book Now
              </PrimaryActionButton>
            </ToastActions>
          </ToastCard>
        </ToastContainer>
      )}
    </AnimatePresence>
  );
};

export default BookingToast; 